import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, Minus, ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";

export function StrategyAnalysis({ strategies, lang }) {
  const [expanded, setExpanded] = useState(null);

  if (!strategies || strategies.length === 0) return null;

  const toggle = (i) => setExpanded(expanded === i ? null : i);

  return (
    <Card className="bg-zinc-900 border-zinc-800 mb-4" data-testid="strategy-analysis">
      <CardHeader className="pb-2 pt-3 px-4">
        <CardTitle className="text-sm text-zinc-300">
          {lang === "ar" ? "تحليل الاستراتيجيات" : "Strategy Analysis"}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-3">
        <div className="space-y-2">
          {strategies.map((s, i) => {
            const isOpen = expanded === i;
            const isSuitable = s.suitable;
            const scorePct = s.score;
            const barColor = scorePct >= 70 ? "bg-green-500" : scorePct >= 45 ? "bg-yellow-500" : "bg-zinc-600";
            const dirIcon = s.direction === "BUY" ? <TrendingUp className="w-3.5 h-3.5 text-green-400" /> : s.direction === "SELL" ? <TrendingDown className="w-3.5 h-3.5 text-red-400" /> : <Minus className="w-3.5 h-3.5 text-zinc-400" />;
            const dirColor = s.direction === "BUY" ? "text-green-400" : s.direction === "SELL" ? "text-red-400" : "text-zinc-400";

            return (
              <div key={i} className={`rounded border transition-all ${isSuitable ? "border-zinc-700 bg-zinc-800/60" : "border-zinc-800 bg-zinc-900/40 opacity-70"}`} data-testid={`strategy-${i}`}>
                <button
                  onClick={() => toggle(i)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left"
                >
                  {/* Score bar */}
                  <div className="w-10 h-10 rounded-lg bg-zinc-800 flex items-center justify-center relative overflow-hidden shrink-0">
                    <div className={`absolute bottom-0 left-0 right-0 ${barColor} transition-all`} style={{ height: `${scorePct}%` }} />
                    <span className="relative text-xs font-bold text-white z-10">{scorePct}%</span>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-zinc-200">
                        {lang === "ar" ? s.name_ar : s.name}
                      </span>
                      {isSuitable && (
                        <Badge className={`text-[10px] px-1.5 py-0 ${s.direction === "BUY" ? "bg-green-700" : s.direction === "SELL" ? "bg-red-700" : "bg-zinc-700"}`}>
                          {dirIcon}
                          <span className="ml-1">{s.direction}</span>
                        </Badge>
                      )}
                    </div>
                    {/* Mini progress bar */}
                    <div className="w-full h-1 bg-zinc-700 rounded-full mt-1">
                      <div className={`h-full rounded-full ${barColor} transition-all`} style={{ width: `${scorePct}%` }} />
                    </div>
                  </div>

                  <div className="shrink-0">
                    {isOpen ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
                  </div>
                </button>

                {isOpen && (
                  <div className="px-3 pb-3 pt-1 border-t border-zinc-800 animate-in slide-in-from-top-1">
                    <p className="text-xs text-zinc-400 mb-2">
                      {lang === "ar" ? s.description_ar : s.description}
                    </p>
                    <div className="space-y-1 mb-2">
                      {(lang === "ar" ? s.reasons_ar : s.reasons).map((r, j) => (
                        <p key={j} className="text-xs text-zinc-300 flex items-start gap-1.5">
                          <span className={`mt-0.5 shrink-0 ${isSuitable ? "text-green-400" : "text-zinc-500"}`}>&#8226;</span>
                          {r}
                        </p>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {s.key_indicators.map((ind, k) => (
                        <Badge key={k} variant="outline" className="text-[10px] border-zinc-700 text-zinc-400">{ind}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
